import { useState, type FormEvent } from "react";

const FORM_ADDR = "https://readdy.ai/api/form/da2304jk8r5te5mqsf30";

const topics = ["使用反馈", "缓存问题", "功能建议", "合作咨询"];

export default function Contact() {
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const hp = (formData.get("website_alt") as string || "").trim();
    if (hp) {
      setFormError("");
      setFormSuccess("感谢你的反馈，我们已收到并会尽快回复！");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    const params = new URLSearchParams();
    formData.forEach((value, key) => params.append(key, value.toString()));

    setSubmitting(true);
    setFormError("");
    setFormSuccess("");
    try {
      const res = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || "";
      if (res.ok && parsed?.code === "OK") {
        setFormSuccess("感谢你的反馈，我们已收到并会尽快回复！");
        form.reset();
      } else {
        setFormError(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setFormError("网络异常，请稍后重试。");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="bg-background-50 py-16 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-14">
          <div>
            <p className="text-sm font-semibold uppercase tracking-widest text-primary-600">
              联系反馈
            </p>
            <h2 className="mt-3 font-heading text-3xl font-black text-foreground-950 md:text-4xl">
              你的声音，让我们做得更好
            </h2>
            <p className="mt-4 text-base leading-relaxed text-foreground-600">
              无论是缓存体验、功能建议还是合作想法，都欢迎留言告诉我们。
              我们会认真阅读每一条反馈，让洒洒漫画网的离线阅读越来越顺手。
            </p>

            <div className="mt-8 space-y-4">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                  <i className="ri-mail-line text-xl" aria-hidden="true"></i>
                </span>
                <div>
                  <p className="text-sm font-semibold text-foreground-900">反馈邮箱</p>
                  <p className="text-sm text-foreground-500">hello@sasa-manhua.example.com</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                  <i className="ri-wechat-line text-xl" aria-hidden="true"></i>
                </span>
                <div>
                  <p className="text-sm font-semibold text-foreground-900">读者社群</p>
                  <p className="text-sm text-foreground-500">官方公众号 / 读者交流群（扫码加入）</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                  <i className="ri-time-line text-xl" aria-hidden="true"></i>
                </span>
                <div>
                  <p className="text-sm font-semibold text-foreground-900">回复时效</p>
                  <p className="text-sm text-foreground-500">工作日 24 小时内回复</p>
                </div>
              </div>
            </div>
          </div>

          <form
            id="contact-form"
            data-readdy-form="contact-form"
            className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-8"
            onSubmit={handleSubmit}
          >
            <h3 className="text-lg font-bold text-foreground-900">给我们留言</h3>
            <p className="mt-1 text-sm text-foreground-500">
              填写下方表单，我们会尽快与你联系。
            </p>

            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="contact-name" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  昵称
                </label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  placeholder="你的昵称"
                  className="w-full rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-300"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  邮箱
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  placeholder="用于接收回复"
                  className="w-full rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-300"
                />
              </div>
            </div>

            <div className="mt-4">
              <label htmlFor="contact-topic" className="mb-1.5 block text-sm font-medium text-foreground-800">
                反馈类型
              </label>
              <select
                id="contact-topic"
                name="topic"
                className="w-full rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-300"
                defaultValue={topics[0]}
              >
                {topics.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>

            <div className="mt-4">
              <label htmlFor="contact-message" className="mb-1.5 block text-sm font-medium text-foreground-800">
                留言内容
              </label>
              <textarea
                id="contact-message"
                name="message"
                required
                maxLength={500}
                rows={5}
                placeholder="请描述你的想法、建议或遇到的问题（500 字以内）"
                className="w-full resize-none rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-300"
              />
              <p className="mt-1 text-right text-xs text-foreground-400">限 500 字</p>
            </div>

            <input
              type="text"
              name="website_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="readdy-hp"
            />

            {formError && (
              <p className="mt-4 rounded-md bg-primary-50 px-4 py-2.5 text-sm text-primary-800">
                {formError}
              </p>
            )}
            {formSuccess && (
              <p className="mt-4 rounded-md bg-accent-100 px-4 py-2.5 text-sm text-accent-900">
                {formSuccess}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="mt-6 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <i className="ri-send-plane-line text-base" aria-hidden="true"></i>
              {submitting ? "提交中…" : "提交反馈"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}